// 10 product quote configurations — every form: 1 question per step + progress bar
// Step types: text, number, slider, options, date, duo, file, contact, rgpd

import {
  Car,
  Home as HomeIcon,
  Heart,
  Landmark,
  PiggyBank,
  Briefcase,
  Truck,
  Shield,
  HeartPulse,
  Zap,
} from "lucide-react";

const RGPD_TEXT =
  "J'accepte que mes données soient utilisées par OPTINA pour traiter ma demande. Données non revendues. Voir Politique de confidentialité.";
const FILE_HINT_DEFAULT = "PDF ou photo — optionnel";

const contactStep = { key: "contact", type: "contact", label: "Vos coordonnées" };
const rgpdStep = { key: "rgpd", type: "rgpd", label: "Confidentialité", text: RGPD_TEXT };

export const PRODUCTS = {
  auto: {
    label: "Assurance Auto",
    icon: Car,
    title: "Assurance Auto",
    intro: "Réduisez votre prime grâce à la mise en concurrence du marché.",
    steps: [
      { key: "marque", type: "text", label: "Marque du véhicule", placeholder: "Ex. Renault, Peugeot, BMW…" },
      { key: "modele_annee", type: "text", label: "Modèle et année", placeholder: "Ex. Clio 2020" },
      {
        key: "bonus_malus",
        type: "slider",
        label: "Coefficient bonus-malus",
        hint: "0.50 = bonus max · 1.00 = départ · 3.50 = malus max",
        min: 0.5,
        max: 3.5,
        step: 0.05,
        defaultValue: 1.0,
        unit: "",
        decimal: true,
      },
      { key: "permis_date", type: "date", label: "Date d'obtention du permis" },
      {
        key: "assureur_actuel",
        type: "duo",
        label: "Assureur actuel et montant mensuel",
        a: { key: "assureur_nom", placeholder: "Ex. AXA, MAIF…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel ou dernier avis d'échéance",
        hint: FILE_HINT_DEFAULT,
      },
      {
        key: "releve_file",
        type: "file",
        label: "Déposez votre relevé d'informations",
        hint: "Document fourni par votre assureur résumant votre historique de conduite (PDF ou photo — optionnel)",
      },
      contactStep,
      rgpdStep,
    ],
  },

  habitation: {
    label: "Assurance Habitation",
    icon: HomeIcon,
    title: "Assurance Habitation",
    intro: "Multirisque habitation au juste prix.",
    steps: [
      {
        key: "type_bien",
        type: "options",
        label: "Type de bien",
        options: [
          { v: "maison", l: "Maison" },
          { v: "appartement", l: "Appartement" },
        ],
      },
      { key: "surface", type: "number", label: "Surface (m²)", placeholder: "Ex. 80", suffix: "m²" },
      {
        key: "statut",
        type: "options",
        label: "Statut",
        options: [
          { v: "proprietaire", l: "Propriétaire" },
          { v: "locataire", l: "Locataire" },
        ],
      },
      {
        key: "sinistres",
        type: "options",
        label: "Sinistres ces 3 dernières années ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "assureur_actuel",
        type: "duo",
        label: "Assureur actuel et montant mensuel",
        a: { key: "assureur_nom", placeholder: "Ex. Groupama…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel ou dernier avis d'échéance",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  sante: {
    label: "Mutuelle Santé",
    icon: Heart,
    title: "Mutuelle Santé",
    intro: "Une couverture sur mesure, adaptée à votre situation.",
    steps: [
      { key: "age", type: "number", label: "Votre âge", placeholder: "Ex. 42", suffix: "ans" },
      {
        key: "statut",
        type: "options",
        label: "Statut",
        options: [
          { v: "salarie", l: "Salarié" },
          { v: "tns", l: "Travailleur indépendant" },
          { v: "retraite", l: "Retraité" },
        ],
      },
      {
        key: "soins_courants",
        type: "options",
        label: "Lunettes ou suivi dentaire régulier ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "hospitalisation",
        type: "options",
        label: "Hospitalisé ces 2 dernières années ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "mutuelle_actuelle",
        type: "duo",
        label: "Mutuelle actuelle et montant mensuel",
        a: { key: "mutuelle_nom", placeholder: "Ex. Harmonie…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel ou dernier avis d'échéance",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  emprunteur: {
    label: "Assurance Emprunteur",
    icon: Landmark,
    title: "Assurance Emprunteur",
    intro: "Renégociez votre assurance de prêt — Loi Lemoine.",
    steps: [
      { key: "loan_amount", type: "number", label: "Montant du prêt immobilier (€)", placeholder: "Ex. 200000", suffix: "€" },
      {
        key: "duration_years",
        type: "slider",
        label: "Durée restante du prêt",
        min: 1,
        max: 30,
        step: 1,
        defaultValue: 20,
        unit: "ans",
      },
      { key: "interest_rate", type: "number", label: "Taux d'intérêt actuel (%)", placeholder: "Ex. 3.5", suffix: "%", decimal: true, step: 0.05 },
      { key: "age", type: "number", label: "Votre âge", placeholder: "Ex. 35", suffix: "ans" },
      {
        key: "smoker",
        type: "options",
        label: "Fumeur ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "profession",
        type: "options",
        label: "Profession",
        options: [
          { v: "salarie", l: "Salarié" },
          { v: "independant", l: "Indépendant" },
          { v: "fonctionnaire", l: "Fonctionnaire" },
          { v: "autre", l: "Autre" },
        ],
      },
      {
        key: "assureur_actuel",
        type: "duo",
        label: "Assureur actuel et montant mensuel",
        a: { key: "assureur_nom", placeholder: "Ex. CNP…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat assurance emprunteur actuel",
        hint: "Permet de calculer précisément vos économies potentielles (PDF ou photo — optionnel)",
      },
      contactStep,
      rgpdStep,
    ],
  },

  per: {
    label: "PER · Épargne Retraite",
    icon: PiggyBank,
    title: "PER — Épargne Retraite",
    intro: "Réduisez vos impôts tout en préparant votre retraite.",
    steps: [
      { key: "age", type: "number", label: "Votre âge", placeholder: "Ex. 45", suffix: "ans" },
      { key: "revenu_annuel", type: "number", label: "Revenu annuel net (€)", placeholder: "Ex. 45000", suffix: "€" },
      {
        key: "tmi",
        type: "options",
        label: "Tranche Marginale d'Imposition",
        options: [
          { v: "11", l: "11 %" },
          { v: "30", l: "30 %" },
          { v: "41", l: "41 %" },
          { v: "45", l: "45 %" },
        ],
      },
      {
        key: "effort_epargne_mensuel",
        type: "slider",
        label: "Effort d'épargne mensuel souhaité",
        min: 50,
        max: 2000,
        step: 10,
        defaultValue: 200,
        unit: "€",
      },
      {
        key: "objectif",
        type: "options",
        label: "Objectif principal",
        options: [
          { v: "impots", l: "Réduire mes impôts" },
          { v: "retraite", l: "Préparer ma retraite" },
          { v: "immobilier", l: "Projet immobilier" },
          { v: "les_trois", l: "Les trois" },
        ],
      },
      {
        key: "contrat_existant",
        type: "options",
        label: "Avez-vous déjà un PER ou contrat d'épargne ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  "rc-pro": {
    label: "RC Professionnelle",
    icon: Briefcase,
    title: "RC Professionnelle",
    intro: "Couvrez votre responsabilité civile professionnelle.",
    steps: [
      {
        key: "secteur",
        type: "select",
        label: "Votre secteur d'activité",
        placeholder: "Choisissez un secteur",
        options: [
          { v: "btp_batiment", l: "BTP / Bâtiment" },
          { v: "medical_sante", l: "Médical & santé" },
          { v: "commerce_artisanat", l: "Commerce & artisanat" },
          { v: "transport", l: "Transport" },
          { v: "profession_liberale", l: "Profession libérale" },
          { v: "restauration", l: "Restauration" },
          { v: "autre", l: "Autre" },
        ],
      },
      { key: "ca_annuel", type: "number", label: "Chiffre d'affaires annuel (€)", placeholder: "Ex. 250000", suffix: "€" },
      { key: "salaries", type: "number", label: "Nombre de salariés", placeholder: "Ex. 5" },
      {
        key: "assureur_actuel",
        type: "duo",
        label: "Assureur actuel et montant mensuel",
        a: { key: "assureur_nom", placeholder: "Ex. Allianz…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel ou dernier avis d'échéance",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  flotte: {
    label: "Flotte Automobile",
    icon: Truck,
    title: "Flotte Automobile",
    intro: "Optimisez l'assurance de votre flotte de véhicules.",
    steps: [
      { key: "nb_vehicules", type: "number", label: "Nombre de véhicules", placeholder: "Ex. 6" },
      {
        key: "type_vehicules",
        type: "options",
        label: "Types de véhicules",
        options: [
          { v: "utilitaire", l: "Utilitaire" },
          { v: "berline", l: "Berline" },
          { v: "camionnette", l: "Camionnette" },
          { v: "mixte", l: "Mixte" },
        ],
      },
      {
        key: "assureur_actuel",
        type: "duo",
        label: "Assureur actuel et montant mensuel",
        a: { key: "assureur_nom", placeholder: "Ex. AXA…", type: "text" },
        b: { key: "montant_mensuel", placeholder: "€/mois", type: "number" },
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat flotte actuel",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  "sante-collective": {
    label: "Mutuelle Santé Collective",
    icon: HeartPulse,
    title: "Mutuelle Santé Collective",
    intro: "La mutuelle entreprise au meilleur tarif pour vos salariés.",
    steps: [
      { key: "nb_salaries", type: "number", label: "Nombre de salariés", placeholder: "Ex. 12" },
      {
        key: "secteur",
        type: "select",
        label: "Votre secteur d'activité",
        placeholder: "Choisissez un secteur",
        options: [
          { v: "btp_batiment", l: "BTP / Bâtiment" },
          { v: "medical_sante", l: "Médical & santé" },
          { v: "commerce_artisanat", l: "Commerce & artisanat" },
          { v: "transport", l: "Transport" },
          { v: "profession_liberale", l: "Profession libérale" },
          { v: "restauration", l: "Restauration" },
          { v: "autre", l: "Autre" },
        ],
      },
      {
        key: "contrat_existant",
        type: "options",
        label: "Avez-vous déjà un contrat collectif ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      { key: "montant_par_salarie", type: "number", label: "Si oui, montant mensuel par salarié (€)", placeholder: "Optionnel", suffix: "€" },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  prevoyance: {
    label: "Prévoyance TNS",
    icon: Shield,
    title: "Prévoyance TNS",
    intro: "Protégez vos revenus en cas d'arrêt, invalidité ou décès.",
    steps: [
      {
        key: "statut",
        type: "options",
        label: "Statut",
        options: [
          { v: "tns", l: "Travailleur indépendant" },
          { v: "dirigeant", l: "Dirigeant de société" },
        ],
      },
      { key: "revenu_mensuel", type: "number", label: "Revenu mensuel net (€)", placeholder: "Ex. 4500", suffix: "€" },
      {
        key: "couverture",
        type: "options",
        label: "Couverture souhaitée",
        options: [
          { v: "arret", l: "Arrêt maladie" },
          { v: "invalidite", l: "Invalidité" },
          { v: "deces", l: "Décès" },
          { v: "les_trois", l: "Les trois" },
        ],
      },
      {
        key: "contrat_existant",
        type: "options",
        label: "Avez-vous déjà un contrat prévoyance ?",
        options: [
          { v: "non", l: "Non" },
          { v: "oui", l: "Oui" },
        ],
      },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre contrat actuel",
        hint: FILE_HINT_DEFAULT,
      },
      contactStep,
      rgpdStep,
    ],
  },

  energie: {
    label: "Optimisation Énergie",
    icon: Zap,
    title: "Optimisation Énergie",
    intro: "Électricité et gaz négociés directement avec les fournisseurs.",
    steps: [
      {
        key: "energy_type",
        type: "options",
        label: "Type d'énergie",
        options: [
          { v: "electricite", l: "Électricité" },
          { v: "gaz", l: "Gaz" },
          { v: "les_deux", l: "Les deux" },
        ],
      },
      {
        key: "current_provider",
        type: "options",
        label: "Fournisseur actuel",
        options: [
          { v: "edf", l: "EDF" },
          { v: "engie", l: "Engie" },
          { v: "total", l: "TotalEnergies" },
          { v: "autre", l: "Autre" },
        ],
      },
      {
        key: "monthly_bill",
        type: "slider",
        label: "Facture mensuelle",
        min: 50,
        max: 2000,
        step: 10,
        defaultValue: 200,
        unit: "€",
      },
      {
        key: "place_type",
        type: "options",
        label: "Type de lieu",
        options: [
          { v: "maison", l: "Maison" },
          { v: "appartement", l: "Appartement" },
          { v: "bureau", l: "Bureau" },
          { v: "local", l: "Local commercial" },
        ],
      },
      { key: "surface", type: "number", label: "Surface (m²)", placeholder: "Ex. 80", suffix: "m²" },
      {
        key: "contract_file",
        type: "file",
        label: "Déposez votre dernière facture énergie",
        hint: "Une facture égale une analyse plus précise et une meilleure négociation (PDF ou photo — optionnel).",
      },
      contactStep,
      rgpdStep,
    ],
  },
};

export const PRODUCT_KEYS = Object.keys(PRODUCTS);
